import { useQuery } from '@apollo/client';
import { IconButton, styled, useTheme } from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { AutocompletePopper } from 'src/components/AutocompletePopper/autocomplete-popper';
import { gql } from 'src/gql';
import {
  Address,
  DEFAULT_CLIENTS_QUERY_LIMIT,
  PartnerGroupRules,
  usePartnerGroupRules,
} from 'src/graphql';
import { useActivationFlowUrl } from 'src/graphql/entities/activation-flow-url';
import { useCopyToClipboard } from 'src/utils/hooks';
import { Platform } from 'src/utils/system';

const SiteShareClientsQuery = gql(`
  query SiteShareClientsQuery($params: QueryParamsWithSearch) @entity(name: "Client") {
    getClient(queryParams: $params) {
      records {
        id
        clientId
        firstName
        lastName
        primaryEmail {
          id
          value
        }
      }
    }
  }
`);

interface ClientOption {
  id: string;
  value: string;
  firstName: string;
  lastName: string;
  email: string;
}

const ClientInfoLine = styled('div')(({ theme }) => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  maxWidth: theme.spacing(16.75),
}));

interface ShareSiteButtonProps {
  address: Address;
  partnerId: number;
}

export const SiteShareButton = ({ address, partnerId }: ShareSiteButtonProps) => {
  const theme = useTheme();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const autocompleteRef = useRef(null);
  const { generateAfMagicLoginUrl } = useActivationFlowUrl();

  const { partnerGroupRules } = usePartnerGroupRules(partnerId);

  const { copyToClipboard } = useCopyToClipboard();

  const filters = useMemo(
    () =>
      address?.addressId
        ? [
            {
              attribute: 'addresses/addressId',
              operator: '=',
              value: address?.addressId || '',
            },
          ]
        : [],
    [address?.addressId],
  );

  const { data } = useQuery(SiteShareClientsQuery, {
    variables: {
      params: {
        filters,
        limit: DEFAULT_CLIENTS_QUERY_LIMIT,
      },
    },
    skip: !filters.length,
  });

  const clientOptions = useMemo(() => {
    return (data?.getClient?.records ?? [])
      .filter((client) => !!client)
      .map((client) => ({
        id: client.clientId,
        value: client.clientId,
        firstName: client.firstName ?? '',
        lastName: client.lastName ?? '',
        email: client.primaryEmail?.value ?? '',
      }));
  }, [data]);

  const openAutocomplete = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorElement(event.currentTarget);
    },
    [setAnchorElement],
  );

  const closeAutocomplete = useCallback(() => {
    if (anchorElement) {
      anchorElement.focus();
    }
    setAnchorElement(null);
  }, [anchorElement, setAnchorElement]);

  const handleCopyActivationFlowUrl = useCallback(
    async (option: ClientOption | null) => {
      if (!option || !address?.addressId) return;

      const url = await generateAfMagicLoginUrl({
        addressId: address.addressId,
        clientId: option.value,
        partnerId,
        email: option.email,
        disableLink: true,
      });

      if (url?.url) {
        await copyToClipboard(url.url, { openInModal: Platform.isSafari });
      }
    },
    [address?.addressId, copyToClipboard, generateAfMagicLoginUrl, partnerId],
  );

  return partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagement] ? (
    <>
      <Tooltip
        title={
          <div>
            Generate Client Activation URL
            <br />
            Click and select a user to
            <br />
            generate a unique URL.
          </div>
        }
      >
        <IconButton
          size="small"
          sx={{
            padding: 0,
          }}
          onClick={openAutocomplete}
          ref={autocompleteRef}
        >
          <DynamicIcon name="share" />
        </IconButton>
      </Tooltip>

      <AutocompletePopper<ClientOption>
        isOpen={Boolean(anchorElement)}
        anchorEl={autocompleteRef}
        closeAutocomplete={closeAutocomplete}
        label="Choose a link to copy"
        placeholder="Select client to copy URL"
        options={clientOptions}
        onChange={handleCopyActivationFlowUrl}
        getOptionLabel={(option) => getFullName(option)}
        getOptionDisabled={(option) => !option.email}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <DynamicIcon name="link" sx={{ transform: 'rotate(135deg)' }} />

            <div style={{ paddingLeft: theme.spacing(0.9) }}>
              <ClientInfoLine>{getFullName(option)}</ClientInfoLine>
              <ClientInfoLine>
                {option.email || 'Unavailable. No email address.'}
              </ClientInfoLine>
            </div>
          </li>
        )}
      />
    </>
  ) : null;
};
